import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import {
  Crop,
  SlidersHorizontal,
  Sparkles,
  RotateCcw,
  RotateCw,
  FlipHorizontal,
  FlipVertical,
  Check,
  X,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Point { x: number; y: number }
interface Area  { x: number; y: number; width: number; height: number }

interface PhotoEditorModalProps {
  isOpen: boolean;
  imageSrc: string;
  onSave: (blob: Blob, previewUrl: string) => void;
  onCancel: () => void;
}

type Tab = 'crop' | 'tune' | 'filters';

interface TuneValues {
  brightness: number;  // 50–150  (100 = normal)
  contrast:   number;  // 50–150
  saturation: number;  // 0–200
  warmth:     number;  // -50–50
}

interface FilterPreset {
  id: string;
  label: string;
  filter: string;
}

// ---------------------------------------------------------------------------
// Filter presets
// ---------------------------------------------------------------------------

const PRESETS: FilterPreset[] = [
  { id: 'original', label: 'Original',  filter: 'none' },
  { id: 'vivid',    label: 'Vivid',     filter: 'brightness(108%) saturate(160%) contrast(112%)' },
  { id: 'warm',     label: 'Warm',      filter: 'brightness(106%) saturate(115%) sepia(28%) contrast(95%)' },
  { id: 'cool',     label: 'Cool',      filter: 'brightness(102%) saturate(80%) hue-rotate(18deg) contrast(108%)' },
  { id: 'fade',     label: 'Fade',      filter: 'brightness(118%) contrast(78%) saturate(72%)' },
  { id: 'bw',       label: 'B&W',       filter: 'grayscale(100%) contrast(115%)' },
  { id: 'sepia',    label: 'Sepia',     filter: 'sepia(75%) brightness(106%) contrast(95%)' },
  { id: 'drama',    label: 'Dramatic',  filter: 'contrast(140%) brightness(88%) saturate(130%)' },
  { id: 'matte',    label: 'Matte',     filter: 'brightness(112%) contrast(82%) saturate(75%) sepia(12%)' },
  { id: 'chrome',   label: 'Chrome',    filter: 'saturate(0%) contrast(125%) brightness(110%)' },
];

// ---------------------------------------------------------------------------
// Canvas helpers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Pixel-based filter application (universal — ctx.filter has poor Safari support)
// ---------------------------------------------------------------------------

function parseFilterFunctions(filterStr: string): Array<{ fn: string; value: number }> {
  const result: Array<{ fn: string; value: number }> = [];
  const regex = /([\w-]+)\(([^)]+)\)/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(filterStr)) !== null) {
    const fn = match[1];
    const arg = match[2].trim();
    let value: number;
    if (arg.endsWith('%')) value = parseFloat(arg) / 100;
    else if (arg.endsWith('deg')) value = parseFloat(arg);
    else value = parseFloat(arg);
    result.push({ fn, value });
  }
  return result;
}

function clampByte(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v | 0;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h, s, l];
}

function hue2rgb(p: number, q: number, t: number): number {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 0.5) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) { const v = l * 255; return [v, v, v]; }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hue2rgb(p, q, h + 1 / 3) * 255, hue2rgb(p, q, h) * 255, hue2rgb(p, q, h - 1 / 3) * 255];
}

function applyFilterFn(fn: string, val: number, r: number, g: number, b: number): [number, number, number] {
  switch (fn) {
    case 'brightness':
      return [r * val, g * val, b * val];
    case 'contrast':
      return [(r - 128) * val + 128, (g - 128) * val + 128, (b - 128) * val + 128];
    case 'saturate': {
      const [h, s, l] = rgbToHsl(r, g, b);
      return hslToRgb(h, Math.min(1, s * val), l);
    }
    case 'grayscale': {
      const gray = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      return [r + (gray - r) * val, g + (gray - g) * val, b + (gray - b) * val];
    }
    case 'sepia':
      return [
        r + (r * 0.393 + g * 0.769 + b * 0.189 - r) * val,
        g + (r * 0.349 + g * 0.686 + b * 0.168 - g) * val,
        b + (r * 0.272 + g * 0.534 + b * 0.131 - b) * val,
      ];
    case 'hue-rotate': {
      const [h, s, l] = rgbToHsl(r, g, b);
      return hslToRgb(((h + val / 360) % 1 + 1) % 1, s, l);
    }
    default:
      return [r, g, b];
  }
}

function applyFilterViaPixels(ctx: CanvasRenderingContext2D, filterStr: string): void {
  if (!filterStr || filterStr === 'none') return;
  const fns = parseFilterFunctions(filterStr);
  if (fns.length === 0) return;
  const { width, height } = ctx.canvas;
  const imageData = ctx.getImageData(0, 0, width, height);
  const d = imageData.data;
  for (let i = 0; i < d.length; i += 4) {
    let r = d[i], g = d[i + 1], b = d[i + 2];
    for (const { fn, value } of fns) {
      [r, g, b] = applyFilterFn(fn, value, r, g, b);
    }
    d[i]     = clampByte(r);
    d[i + 1] = clampByte(g);
    d[i + 2] = clampByte(b);
  }
  ctx.putImageData(imageData, 0, 0);
}

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = url;
  });

const toRad = (deg: number) => (deg * Math.PI) / 180;

/** Axis-aligned bounding box of the image after rotation */
function rotatedBBox(w: number, h: number, rot: number) {
  const r = toRad(rot);
  return {
    width:  Math.abs(Math.cos(r) * w) + Math.abs(Math.sin(r) * h),
    height: Math.abs(Math.sin(r) * w) + Math.abs(Math.cos(r) * h),
  };
}

async function getCroppedImg(
  src: string,
  crop: Area,
  rotation: number,
  flipH: boolean,
  flipV: boolean,
  filterStr: string,
): Promise<Blob> {
  const image = await createImage(src);

  // Step 1: draw the image rotated + flipped onto a canvas sized to the
  // bounding box. react-easy-crop's croppedAreaPixels are in this same
  // bounding-box coordinate space, so getImageData coords match directly.
  const { width: bw, height: bh } = rotatedBBox(image.width, image.height, rotation);
  const rotCanvas = document.createElement('canvas');
  rotCanvas.width  = Math.round(bw);
  rotCanvas.height = Math.round(bh);
  const rotCtx = rotCanvas.getContext('2d')!;
  rotCtx.translate(bw / 2, bh / 2);
  rotCtx.rotate(toRad(rotation));
  rotCtx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
  rotCtx.translate(-image.width / 2, -image.height / 2);
  rotCtx.drawImage(image, 0, 0);

  // Step 2: extract the crop region via getImageData / putImageData
  // (putImageData writes raw pixels, unaffected by ctx.filter, which is
  // what we want here — filter is applied in step 3)
  const pixelData = rotCtx.getImageData(
    Math.round(crop.x),
    Math.round(crop.y),
    Math.round(crop.width),
    Math.round(crop.height),
  );
  rotCanvas.width  = Math.round(crop.width);
  rotCanvas.height = Math.round(crop.height);
  rotCtx.putImageData(pixelData, 0, 0);

  // Step 3: apply filter via pixel manipulation (works in all browsers;
  // ctx.filter has limited support in older Safari/iOS WebView)
  applyFilterViaPixels(rotCtx, filterStr);

  return new Promise((resolve, reject) =>
    rotCanvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Canvas is empty'))),
      'image/jpeg', 0.92,
    )
  );
}

function tuneToFilter(t: TuneValues): string {
  const sepia = t.warmth > 0 ? `sepia(${t.warmth * 0.6}%)` : '';
  const hue   = t.warmth < 0 ? `hue-rotate(${Math.abs(t.warmth) * 0.4}deg)` : '';
  return `brightness(${t.brightness}%) contrast(${t.contrast}%) saturate(${t.saturation}%) ${sepia}${hue}`.trim();
}

// ---------------------------------------------------------------------------
// Aspect ratio options with visual icons
// ---------------------------------------------------------------------------

const ASPECT_OPTIONS = [
  {
    label: 'Square',
    value: 1,
    icon: (
      <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="3" y="3" width="14" height="14" rx="1.5" />
      </svg>
    ),
  },
  {
    label: 'Portrait',
    value: 4 / 5,
    icon: (
      <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="5" y="2" width="10" height="16" rx="1.5" />
      </svg>
    ),
  },
  {
    label: 'Wide',
    value: 16 / 9,
    icon: (
      <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="1" y="5" width="18" height="10" rx="1.5" />
      </svg>
    ),
  },
] as const;

// ---------------------------------------------------------------------------
// Filter thumbnail
// ---------------------------------------------------------------------------

const FilterThumbnail: React.FC<{
  src: string;
  preset: FilterPreset;
  active: boolean;
  onClick: () => void;
}> = ({ src, preset, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex flex-col items-center gap-1.5 flex-shrink-0 focus:outline-none touch-manipulation"
  >
    <div className={cn(
      'w-20 h-20 rounded-xl overflow-hidden border-2 transition-all',
      active ? 'border-[#cfef00] scale-105' : 'border-transparent',
    )}>
      <img
        src={src}
        alt={preset.label}
        draggable={false}
        style={{ filter: preset.filter === 'none' ? undefined : preset.filter }}
        className="w-full h-full object-cover"
      />
    </div>
    <span className={cn('text-xs font-medium', active ? 'text-[#cfef00]' : 'text-zinc-400')}>
      {preset.label}
    </span>
  </button>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const PhotoEditorModal: React.FC<PhotoEditorModalProps> = ({
  isOpen,
  imageSrc,
  onSave,
  onCancel,
}) => {
  const [activeTab, setActiveTab]     = useState<Tab>('crop');

  const [crop, setCrop]               = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom]               = useState(1);
  const [rotation, setRotation]       = useState(0);
  const [flipH, setFlipH]             = useState(false);
  const [flipV, setFlipV]             = useState(false);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [aspect, setAspect]           = useState<number>(1);

  const [tune, setTune]               = useState<TuneValues>({ brightness: 100, contrast: 100, saturation: 100, warmth: 0 });
  const [activePreset, setActivePreset] = useState('original');
  const [filterMode, setFilterMode]     = useState<'tune' | 'preset'>('tune');

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setFlipH(false);
      setFlipV(false);
      setTune({ brightness: 100, contrast: 100, saturation: 100, warmth: 0 });
      setActivePreset('original');
      setFilterMode('tune');
      setActiveTab('crop');
      setCroppedArea(null);
      setAspect(1);
    }
  }, [isOpen, imageSrc]);

  const handleCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedArea(pixels);
  }, []);

  const activeFilterStr = filterMode === 'preset'
    ? (PRESETS.find((p) => p.id === activePreset)?.filter ?? 'none')
    : tuneToFilter(tune);

  const handleSave = async () => {
    if (!croppedArea) return;
    setIsSaving(true);
    try {
      const blob = await getCroppedImg(imageSrc, croppedArea, rotation, flipH, flipV, activeFilterStr);
      onSave(blob, URL.createObjectURL(blob));
    } catch (err) {
      console.error('[PhotoEditor] Save failed:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent
        className="fixed inset-0 w-screen h-[100dvh] max-w-none max-h-none m-0 p-0 translate-x-0 translate-y-0 left-0 top-0 rounded-none border-none bg-black flex flex-col gap-0 [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={onCancel}
      >
        {/* Header — respects iOS status bar / notch */}
        <div
          className="flex-shrink-0 flex items-center justify-between px-4 pb-3"
          style={{ paddingTop: 'max(20px, env(safe-area-inset-top))' }}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="flex items-center justify-center w-11 h-11 rounded-full bg-white/10 active:bg-white/20 transition-colors text-white touch-manipulation"
          >
            <X className="h-5 w-5" />
          </button>

          <span className="text-white font-semibold text-base tracking-wide">Edit Photo</span>

          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !croppedArea}
            className={cn(
              'flex items-center justify-center w-11 h-11 rounded-full transition-colors touch-manipulation',
              isSaving || !croppedArea
                ? 'bg-white/10 text-white/40'
                : 'bg-[#cfef00] text-black active:bg-[#d6f500]',
            )}
          >
            {isSaving ? (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
            ) : (
              <Check className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Preview */}
        <div className="flex-1 relative min-h-0 bg-black">
          {activeTab === 'crop' ? (
            <div
              className="absolute inset-0"
              style={{ transform: `scaleX(${flipH ? -1 : 1}) scaleY(${flipV ? -1 : 1})` }}
            >
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={aspect}
                minZoom={1}
                maxZoom={4}
                restrictPosition={true}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={handleCropComplete}
                showGrid
                cropShape="rect"
                style={{
                  containerStyle: { background: 'black' },
                  cropAreaStyle: {
                    border: '2px solid #cfef00',
                    boxShadow: '0 0 0 9999px rgba(0,0,0,0.7)',
                  },
                }}
              />
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <img
                src={imageSrc}
                alt="Preview"
                draggable={false}
                className="max-w-full max-h-full object-contain select-none"
                style={{
                  filter: activeFilterStr === 'none' ? undefined : activeFilterStr,
                  transform: `scaleX(${flipH ? -1 : 1}) scaleY(${flipV ? -1 : 1})`,
                }}
              />
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex-shrink-0 bg-zinc-950 border-t border-zinc-800">

          {/* Crop controls */}
          {activeTab === 'crop' && (
            <div className="px-4 pt-3 pb-2 space-y-3">
              {/* Aspect ratio — icon + label buttons */}
              <div className="flex gap-2">
                {ASPECT_OPTIONS.map((a) => (
                  <button
                    key={a.label}
                    type="button"
                    onClick={() => { setAspect(a.value); setCrop({ x: 0, y: 0 }); setZoom(1); setCroppedArea(null); }}
                    className={cn(
                      'flex-1 flex flex-col items-center justify-center gap-1 min-h-[52px] rounded-xl text-xs font-medium transition-colors touch-manipulation',
                      aspect === a.value ? 'bg-[#cfef00] text-black' : 'bg-zinc-800 active:bg-zinc-700 text-zinc-300',
                    )}
                  >
                    {a.icon}
                    <span>{a.label}</span>
                  </button>
                ))}
              </div>

              {/* Zoom */}
              <div className="space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-xs text-zinc-400 font-medium">Zoom</span>
                  <span className="text-xs text-zinc-500">{zoom.toFixed(1)}×</span>
                </div>
                <Slider value={[zoom]} onValueChange={([v]) => setZoom(v)} min={1} max={4} step={0.05}
                  className="[&_[role=slider]]:bg-[#cfef00] [&_[role=slider]]:border-[#cfef00] [&_[role=slider]]:w-5 [&_[role=slider]]:h-5" />
              </div>

              {/* Rotate + Flip */}
              <div className="flex gap-2">
                <button type="button" onClick={() => setRotation((r) => r - 90)}
                  className="flex-1 flex items-center justify-center gap-1.5 min-h-[44px] rounded-xl bg-zinc-800 active:bg-zinc-700 text-white text-xs font-medium transition-colors touch-manipulation">
                  <RotateCcw className="h-4 w-4" /><span>-90°</span>
                </button>
                <button type="button" onClick={() => setRotation((r) => r + 90)}
                  className="flex-1 flex items-center justify-center gap-1.5 min-h-[44px] rounded-xl bg-zinc-800 active:bg-zinc-700 text-white text-xs font-medium transition-colors touch-manipulation">
                  <RotateCw className="h-4 w-4" /><span>+90°</span>
                </button>
                <div className="w-px bg-zinc-700 self-stretch mx-0.5" />
                <button type="button" onClick={() => setFlipH((v) => !v)}
                  className={cn('flex-1 flex items-center justify-center gap-1.5 min-h-[44px] rounded-xl text-xs font-medium transition-colors touch-manipulation',
                    flipH ? 'bg-[#cfef00] text-black' : 'bg-zinc-800 active:bg-zinc-700 text-white')}>
                  <FlipHorizontal className="h-4 w-4" /><span>Flip H</span>
                </button>
                <button type="button" onClick={() => setFlipV((v) => !v)}
                  className={cn('flex-1 flex items-center justify-center gap-1.5 min-h-[44px] rounded-xl text-xs font-medium transition-colors touch-manipulation',
                    flipV ? 'bg-[#cfef00] text-black' : 'bg-zinc-800 active:bg-zinc-700 text-white')}>
                  <FlipVertical className="h-4 w-4" /><span>Flip V</span>
                </button>
              </div>

              {/* Fine rotation slider */}
              <div className="space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-xs text-zinc-400 font-medium">Rotate</span>
                  <span className="text-xs text-zinc-500">{(rotation % 360).toFixed(0)}°</span>
                </div>
                <Slider value={[rotation % 360 > 180 ? (rotation % 360) - 360 : rotation % 360]}
                  onValueChange={([v]) => setRotation(v)} min={-45} max={45} step={0.5}
                  className="[&_[role=slider]]:bg-[#cfef00] [&_[role=slider]]:border-[#cfef00] [&_[role=slider]]:w-5 [&_[role=slider]]:h-5" />
              </div>
            </div>
          )}

          {/* Tune controls */}
          {activeTab === 'tune' && (
            <div className="px-4 pt-3 pb-2 space-y-3">
              {([
                { key: 'brightness' as const, label: 'Brightness', icon: '☀️', min: 50,  max: 150 },
                { key: 'contrast'   as const, label: 'Contrast',   icon: '◑',  min: 50,  max: 150 },
                { key: 'saturation' as const, label: 'Saturation', icon: '🎨', min: 0,   max: 200 },
                { key: 'warmth'     as const, label: 'Warmth',     icon: '🌡', min: -50, max: 50  },
              ]).map(({ key, label, icon, min, max }) => (
                <div key={key} className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-xs text-zinc-400 font-medium flex items-center gap-1.5">
                      <span>{icon}</span>{label}
                    </span>
                    <span className="text-xs text-zinc-500">
                      {key === 'warmth' && tune[key] > 0 ? `+${tune[key]}` : tune[key]}
                    </span>
                  </div>
                  <Slider value={[tune[key]]}
                    onValueChange={([v]) => { setTune((p) => ({ ...p, [key]: v })); setFilterMode('tune'); }}
                    min={min} max={max} step={1}
                    className="[&_[role=slider]]:bg-[#cfef00] [&_[role=slider]]:border-[#cfef00] [&_[role=slider]]:w-5 [&_[role=slider]]:h-5" />
                </div>
              ))}
            </div>
          )}

          {/* Filter presets */}
          {activeTab === 'filters' && (
            <div className="py-4 pl-4">
              <div className="flex gap-3 overflow-x-auto pb-2 pr-4 snap-x snap-mandatory scrollbar-none">
                {PRESETS.map((p) => (
                  <FilterThumbnail key={p.id} src={imageSrc} preset={p}
                    active={filterMode === 'preset' && activePreset === p.id}
                    onClick={() => { setActivePreset(p.id); setFilterMode('preset'); }} />
                ))}
              </div>
            </div>
          )}

          {/* Tab bar — respects iOS home indicator */}
          <div
            className="flex border-t border-zinc-800"
            style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}
          >
            {([
              { id: 'crop'    as Tab, label: 'Crop',    Icon: Crop              },
              { id: 'tune'    as Tab, label: 'Tune',    Icon: SlidersHorizontal },
              { id: 'filters' as Tab, label: 'Filters', Icon: Sparkles          },
            ]).map(({ id, label, Icon }) => (
              <button key={id} type="button" onClick={() => setActiveTab(id)}
                className={cn(
                  'flex-1 flex flex-col items-center justify-center gap-1 py-3 min-h-[52px] transition-colors touch-manipulation',
                  activeTab === id ? 'text-[#cfef00]' : 'text-zinc-500 active:text-zinc-300',
                )}>
                <Icon className="h-5 w-5" />
                <span className="text-[10px] font-medium">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
